import React, { useState, useEffect } from 'react';
import TouchButton from '../ui/TouchButton';

const PWAInstaller = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    setIsStandalone(
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setShowInstallPrompt(false);
      }
      
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    // Don't show again for this session
    sessionStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Don't show if already installed or dismissed
  if (isStandalone || isInstalled || !showInstallPrompt) {
    return null;
  }

  // Check if user has dismissed this session
  if (sessionStorage.getItem('pwa-install-dismissed')) {
    return null;
  }

  const promptStyles = {
    position: 'fixed',
    bottom: '20px',
    left: '16px',
    right: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    zIndex: 1000,
    maxWidth: '400px',
    margin: '0 auto',
    border: '1px solid #e0e0e0'
  };

  const headerStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px'
  };

  const iconStyles = {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: '#2196F3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: '#ffffff'
  };

  const titleStyles = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#212121',
    margin: 0
  };

  const descriptionStyles = {
    fontSize: '14px',
    color: '#666666',
    lineHeight: '1.4',
    marginBottom: '16px'
  };

  const buttonContainerStyles = {
    display: 'flex',
    gap: '12px',
    flexDirection: 'column'
  };

  const featureListStyles = {
    listStyle: 'none',
    padding: 0,
    margin: '12px 0 16px 0'
  };

  const featureItemStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 0',
    fontSize: '13px',
    color: '#666666'
  };

  const checkIconStyles = {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: '#4CAF50',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 'bold'
  };

  const dismissButtonStyles = {
    position: 'absolute',
    top: '12px',
    right: '12px',
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: '#999999',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  return (
    <div style={promptStyles}>
      <button
        style={dismissButtonStyles}
        onClick={handleDismiss}
        aria-label="Dismiss install prompt"
      >
        ×
      </button>

      <div style={headerStyles}>
        <div style={iconStyles}>
          🛡️
        </div>
        <div>
          <h3 style={titleStyles}>Install Travel Safety</h3>
          <div style={{ fontSize: '12px', color: '#999999' }}>
            Quick access from your home screen
          </div>
        </div>
      </div>

      <p style={descriptionStyles}>
        Get faster access and a better experience with our app. Works offline and sends notifications about safety updates.
      </p>

      <ul style={featureListStyles}>
        <li style={featureItemStyles}>
          <div style={checkIconStyles}>✓</div>
          <span>Works offline with cached maps</span>
        </li>
        <li style={featureItemStyles}>
          <div style={checkIconStyles}>✓</div>
          <span>Instant loading and smooth navigation</span>
        </li>
        <li style={featureItemStyles}>
          <div style={checkIconStyles}>✓</div>
          <span>Push notifications for safety alerts</span>
        </li>
        <li style={featureItemStyles}>
          <div style={checkIconStyles}>✓</div>
          <span>No app store required</span>
        </li>
      </ul>

      <div style={buttonContainerStyles}>
        <TouchButton
          onClick={handleInstallClick}
          variant="primary"
          fullWidth
          icon="📱"
        >
          Install App
        </TouchButton>
        
        <TouchButton
          onClick={handleDismiss}
          variant="ghost"
          fullWidth
        >
          Not now
        </TouchButton>
      </div>
    </div>
  );
};

// PWA status indicator for debugging
export const PWAStatus = () => {
  const [status, setStatus] = useState({
    isInstalled: false,
    isStandalone: false,
    serviceWorkerReady: false,
    isOnline: navigator.onLine
  });

  useEffect(() => {
    // Check installation status
    setStatus(prev => ({
      ...prev,
      isStandalone: 
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true
    }));

    // Check service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(() => {
        setStatus(prev => ({ ...prev, serviceWorkerReady: true }));
      });
    }

    // Listen for online/offline events
    const handleOnline = () => setStatus(prev => ({ ...prev, isOnline: true }));
    const handleOffline = () => setStatus(prev => ({ ...prev, isOnline: false }));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const statusStyles = {
    position: 'fixed',
    top: '10px',
    right: '10px',
    backgroundColor: '#000000',
    color: '#ffffff',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    zIndex: 10000,
    fontFamily: 'monospace'
  };

  return (
    <div style={statusStyles}>
      PWA: {status.isStandalone ? '✓' : '✗'} |
      SW: {status.serviceWorkerReady ? '✓' : '✗'} |
      Online: {status.isOnline ? '✓' : '✗'}
    </div>
  );
};

export default PWAInstaller;